import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { TestStep } from './test-executor.service';

export interface RecordedAction {
  seq: number;
  stepId: string;
  timestamp: string;
  eventType: 'click' | 'input' | 'change' | 'keypress' | 'navigation';
  action: 'fill' | 'click' | 'select' | 'navigate' | 'wait' | 'pressEnter' | 'check' | 'uncheck';
  element: {
    tag: string;
    type: string;
    id: string;
    name: string;
    automationId: string;
    testId: string;
    role: string;
    className: string;
    placeholder: string;
    ariaLabel: string;
    text: string;
  };
  data: {
    value?: string;
    text?: string;
    isPassword?: boolean;
    selectedValue?: string;
    selectedText?: string;
    key?: string;
    rowLabel?: string;
    category?: string;
  };
  url: string;
  pageTitle: string;
  screenshot?: string;
  tableContext?: {
    isTableInteraction: boolean;
    tableId: string;
    tableSelector: string;
    tableAutomationId: string;
    cellRowIndex: number;
    cellColIndex: number;
    columnName: string;
    cellValue: string;
    cellTag: string;
  };
}

export interface RecordingSession {
  recordingId: string;
  runName: string;
  description: string;
  recordedAt: string;
  recordedBy?: string;
  baseUrl: string;
  status: 'idle' | 'recording' | 'paused' | 'completed';
  actions: RecordedAction[];
  executableSteps: TestStep[];
  dataParameters: Record<string, any>;
  metadata: {
    totalSteps: number;
    estimatedDuration: number;
    tags: string[];
    category?: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class ActionRecorderService {
  private recordingSession$ = new BehaviorSubject<RecordingSession | null>(null);
  private isRecording$ = new BehaviorSubject<boolean>(false);
  private liveActions$ = new BehaviorSubject<RecordedAction[]>([]);
  private liveExecutableSteps$ = new BehaviorSubject<TestStep[]>([]);
  // Insert-record support: position to insert the next captured action
  private insertRecordIndex: number | null = null; // insert AFTER this index
  private insertRecordComplete$ = new BehaviorSubject<number | null>(null);
  // When set, the next verification step will be inserted at this index instead of appended
  pendingInsertIndex: number | null = null;

  // NEW: Table interaction detection
  private tableInteractionDetected$ = new BehaviorSubject<{
    tableSelector: string;
    tableId: string;
    columnName: string;
    cellValue: string;
    timestamp: string;
  } | null>(null);

  constructor() {
    // Set up the action listener ONCE when service is created
    this.setupActionListener();
  }

  get currentSession(): Observable<RecordingSession | null> {
    return this.recordingSession$.asObservable();
  }

  get isRecording(): Observable<boolean> {
    return this.isRecording$.asObservable();
  }

  get liveActions(): Observable<RecordedAction[]> {
    return this.liveActions$.asObservable();
  }

  get liveExecutableSteps(): Observable<TestStep[]> {
    return this.liveExecutableSteps$.asObservable();
  }

  // Insert-record completion observable
  get insertRecordComplete(): Observable<number | null> {
    return this.insertRecordComplete$.asObservable();
  }

  resetInsertRecordComplete(): void {
    this.insertRecordComplete$.next(null);
  }

  // NEW: Table interaction observable
  get tableInteractionDetected(): Observable<any> {
    return this.tableInteractionDetected$.asObservable();
  }

  /**
   * Start recording user actions
   */
  async startRecording(runName: string, baseUrl?: string, description?: string, customRecordingId?: string): Promise<RecordingSession> {
    const recordingId = customRecordingId || this.generateId();
    
    const session: RecordingSession = {
      recordingId,
      runName,
      description: description || '',
      recordedAt: new Date().toISOString(),
      baseUrl: baseUrl || window.location.origin,
      status: 'recording',
      actions: [],
      executableSteps: [],
      dataParameters: {},
      metadata: {
        totalSteps: 0,
        estimatedDuration: 0,
        tags: [],
        category: 'recorded'
      }
    };

    this.recordingSession$.next(session);
    this.isRecording$.next(true);
    this.liveActions$.next([]);
    this.liveExecutableSteps$.next([]);

    // Enable recording in Electron
    await (window as any).host?.recordingEnable(true, {
      recordingId,
      runName,
      baseUrl
    });

    return session;
  }

  /**
   * Stop recording and finalize session
   */
  async stopRecording(): Promise<RecordingSession | null> {
    const session = this.recordingSession$.value;
    if (!session) return null;

    this.isRecording$.next(false);
    session.status = 'completed';
    session.metadata.totalSteps = session.executableSteps.length;

    // Disable recording in Electron
    await (window as any).host?.recordingEnable(false);

    // Save recording to file
    // const savedPath = await this.saveRecording(session);

    this.recordingSession$.next(session);
    return session;
  }

  /**
   * Pause/Resume recording
   */
  async pauseRecording(): Promise<void> {
    const session = this.recordingSession$.value;
    if (!session) return;

    if (session.status === 'recording') {
      session.status = 'paused';
      this.isRecording$.next(false);
    } else if (session.status === 'paused') {
      session.status = 'recording';
      this.isRecording$.next(true);
    }

    this.recordingSession$.next(session);
  }

  /**
   * Add a recorded action to the session (with intelligent deduplication)
   */
  addAction(action: RecordedAction): void {
    const session = this.recordingSession$.value;
    const isInsertRecord = this.insertRecordIndex !== null && this.insertRecordIndex >= 0;
    if (!session || (!isInsertRecord && session.status !== 'recording')) return;

    // NEW: Check for table interaction and show notification
    const tableCtx = (action as any).tableContext;
    if (tableCtx && tableCtx.isTableInteraction && action.action === 'click') {
      // Store table context for potential table validation
      if (!session.metadata) session.metadata = { totalSteps: 0, estimatedDuration: 0, tags: [] };
      (session.metadata as any).lastTableInteraction = {
        tableSelector: tableCtx.tableSelector,
        tableId: tableCtx.tableId,
        columnName: tableCtx.columnName,
        cellValue: tableCtx.cellValue,
        rowIndex: tableCtx.cellRowIndex,
        colIndex: tableCtx.cellColIndex,
        timestamp: new Date().toISOString()
      };
      
      // Emit table interaction for UI notification
      this.tableInteractionDetected$.next({
        tableSelector: tableCtx.tableSelector,
        tableId: tableCtx.tableId || 'Table',
        columnName: tableCtx.columnName,
        cellValue: tableCtx.cellValue,
        timestamp: new Date().toISOString()
      });
      
      // Show browser notification (optional - can be enabled later)
      // This would require injecting a notification service
      // For now, just log for Angular to potentially react to
    }

    // SAFETY: Ignore fill actions without a value (not relevant for playback)
    if (action.action === 'fill') {
      const v = (action as any).data?.value;
      const hasValue = typeof v === 'string' ? v.trim().length > 0 : v !== undefined && v !== null;
      if (!hasValue) {
        return;
      }
    }

    // If inserting a new step after a given index, insert (bypass dedup)
    if (isInsertRecord) {
      const baseIdx = this.insertRecordIndex as number;
      const insertPos = baseIdx + 1; // always after
      const newStep = this.transformToExecutableStep(action);
      if (newStep) {
        session.actions.splice(insertPos, 0, action);
        session.executableSteps.splice(insertPos, 0, newStep);
        this.liveActions$.next([...session.actions]);
        this.liveExecutableSteps$.next([...session.executableSteps]);
        session.metadata.totalSteps = session.executableSteps.length;
        this.recordingSession$.next(session);
      }
      // Notify completion and clear flag
      try { this.insertRecordComplete$.next(insertPos); } catch {}
      this.insertRecordIndex = null;
      return;
    }

    // INTELLIGENT DEDUPLICATION: Check against last 3 actions to avoid rapid duplicates
    const recentActions = session.actions.slice(-3);
    const isDuplicate = recentActions.some(recentAction => 
      this.isDuplicateAction(recentAction, action)
    );

    if (isDuplicate) {
      // For fill actions, UPDATE the last matching action's value instead of skipping
      if (action.action === 'fill') {
        // Find last matching fill action (reverse search)
        let lastMatchingIndex = -1;
        for (let i = session.actions.length - 1; i >= 0; i--) {
          if (session.actions[i].action === 'fill' && 
              this.isSameElement(session.actions[i].element, action.element)) {
            lastMatchingIndex = i;
            break;
          }
        }
        
        if (lastMatchingIndex !== -1) {
          session.actions[lastMatchingIndex] = action;
          
          // Update the corresponding executable step
          const executableStep = this.transformToExecutableStep(action);
          if (executableStep && session.executableSteps[lastMatchingIndex]) {
            session.executableSteps[lastMatchingIndex] = executableStep;
            this.liveExecutableSteps$.next([...session.executableSteps]);
          }
          
          this.recordingSession$.next(session);
        }
      }
      return;
    }

    // Default path: append new action
    session.actions.push(action);
    this.liveActions$.next([...session.actions]);

    const executableStep = this.transformToExecutableStep(action);
    if (executableStep) {
      session.executableSteps.push(executableStep);
      this.liveExecutableSteps$.next([...session.executableSteps]);
    }

    this.recordingSession$.next(session);
  }

  /**
   * Insert step at pendingInsertIndex if set, otherwise push to end.
   * Auto-resets pendingInsertIndex after use.
   */
  private insertOrPushStep(session: RecordingSession, step: TestStep): void {
    if (this.pendingInsertIndex !== null && this.pendingInsertIndex >= 0 && this.pendingInsertIndex <= session.executableSteps.length) {
      session.executableSteps.splice(this.pendingInsertIndex, 0, step);
      this.pendingInsertIndex = null;
    } else {
      session.executableSteps.push(step);
    }
  }

  /**
   * Create and append a verification/assertion step.
   * Minimal API to support: visible check (no expected), or text/value check (expected in value).
   */
  addVerification(config: {
    assert?: boolean;
    element?: RecordedAction['element'];
    expected?: string; // when omitted, visibility is verified
    operator?: 'equals' | 'contains' | 'containsAll' | 'matches' | 'is';
    timeoutMs?: number;
  }): void {
    const session = this.recordingSession$.value;
    if (!session) return;

    // Clean up trailing duplicate clicks (common when selecting an element to verify)
    this.removeTrailingDuplicateClicks();

    const element = config.element || ({} as any);
    const selector = this.buildSelector(element);
    if (config.operator) {
      (selector as any).operator = config.operator;
    }

    const step: TestStep = {
      id: `verify-${Date.now()}`,
      action: (config.assert ? 'assert' : 'verify') as any,
      selector,
      value: config.expected || '',
      text: (element as any)['labelText'] || element.name || element.ariaLabel || element.placeholder || '',
      timeout: config.timeoutMs || 8000,
      description: `${config.assert ? 'Assert' : 'Verify'} ${config.expected ? 'value' : 'visibility'} for ${((element as any)['labelText'] || element.name || element.ariaLabel || element.placeholder || 'element')}`
    };

    this.insertOrPushStep(session, step);
    this.liveExecutableSteps$.next([...session.executableSteps]);
    this.recordingSession$.next(session);
  }

  /**
   * Add a page-level text verification/assertion without selecting an element.
   * Uses the runner's special 'page-content' handling to search full page text.
   */
  addPageVerification(expectedText: string, assert: boolean = false, timeoutMs: number = 8000): void {
    const session = this.recordingSession$.value;
    if (!session) return;

    const text = (expectedText || '').trim();
    if (!text) return;

    const step: TestStep = {
      id: `verify-${Date.now()}`,
      action: (assert ? 'assert' : 'verify') as any,
      selector: { css: 'body' },
      value: text,
      text: 'page-content', // Special marker handled by executor
      timeout: timeoutMs,
      description: `${assert ? 'Assert' : 'Verify'} page contains "${text}"`
    };

    this.insertOrPushStep(session, step);
    this.liveExecutableSteps$.next([...session.executableSteps]);
    this.recordingSession$.next(session);
  }

  /**
   * Add a NOT-EXIST verification for an element (element should be absent from page).
   * This is for negative validations where absence of an element indicates success.
   */
  addNotExistVerification(config: {
    elementName: string;
    assert?: boolean;
    absenceTimeout?: number;
    description?: string;
  }): void {
    const session = this.recordingSession$.value;
    if (!session) return;

    const elementName = (config.elementName || '').trim();
    if (!elementName) return;

    const step: TestStep = {
      id: `verify-not-exist-${Date.now()}`,
      action: (config.assert ? 'assertNotExist' : 'verifyNotExist') as any,
      selector: {
        name: elementName,
        expectAbsent: true,
        absenceTimeout: config.absenceTimeout || 3000
      } as any,
      value: '',
      text: elementName,
      timeout: config.absenceTimeout || 3000,
      description: config.description || `${config.assert ? 'Assert' : 'Verify'} ${elementName} does not exist`
    };

    this.insertOrPushStep(session, step);
    this.liveExecutableSteps$.next([...session.executableSteps]);
    this.recordingSession$.next(session);
  }

  /**
   * Add a page-level NOT-CONTAIN verification (page should NOT have specific text).
   * This is for negative validations where absence of text indicates success.
   */
  addPageNotContainVerification(expectedText: string, assert: boolean = false, absenceTimeout: number = 3000): void {
    const session = this.recordingSession$.value;
    if (!session) return;

    const text = (expectedText || '').trim();
    if (!text) return;

    const step: TestStep = {
      id: `verify-not-contain-${Date.now()}`,
      action: (assert ? 'assertNotExist' : 'verifyNotExist') as any,
      selector: { 
        css: 'body',
        expectAbsent: true,
        absenceTimeout: absenceTimeout
      } as any,
      value: text,
      text: 'page-content',
      timeout: absenceTimeout,
      description: `${assert ? 'Assert' : 'Verify'} page does not contain "${text}"`
    };

    this.insertOrPushStep(session, step);
    this.liveExecutableSteps$.next([...session.executableSteps]);
    this.recordingSession$.next(session);
  }

  /**
   * Add table NOT-CONTAINS validation (table should NOT have specific value).
   * Supports pagination with searchAllPages flag.
   */
  addTableNotContainsValidation(config: {
    searchText: string;
    searchAllPages?: boolean;
    tableSelector?: string;
    description?: string;
  }): void {
    const session = this.recordingSession$.value;
    if (!session) return;

    const text = (config.searchText || '').trim();
    if (!text) return;

    const step: TestStep = {
      id: `verify-table-not-contains-${Date.now()}`,
      action: 'verifyTableNotContains',
      selector: {
        tableValidation: 'notContains' as any,
        tableSelector: config.tableSelector,
        searchAllPages: config.searchAllPages || false
      } as any,
      value: text,
      timeout: 10000,
      description: config.description || `Verify table does not contain "${text}"${config.searchAllPages ? ' across all pages' : ''}`
    };

    this.insertOrPushStep(session, step);
    this.liveExecutableSteps$.next([...session.executableSteps]);
    this.recordingSession$.next(session);
  }

  /**
   * Add column-specific NOT-IN validation (column should NOT contain specific value).
   * Supports pagination with searchAllPages flag.
   */
  addTableColumnNotInValidation(config: {
    columnIdentifier: string;
    searchText: string;
    searchAllPages?: boolean;
    tableSelector?: string;
    description?: string;
  }): void {
    const session = this.recordingSession$.value;
    if (!session) return;

    const text = (config.searchText || '').trim();
    const column = (config.columnIdentifier || '').trim();
    if (!text || !column) return;

    const step: TestStep = {
      id: `verify-column-not-in-${Date.now()}`,
      action: 'verifyTableNotInColumn',
      selector: {
        tableValidation: 'notInColumn' as any,
        columnIdentifier: column,
        tableSelector: config.tableSelector,
        searchAllPages: config.searchAllPages || false
      } as any,
      value: text,
      timeout: 10000,
      description: config.description || `Verify column "${column}" does not contain "${text}"${config.searchAllPages ? ' across all pages' : ''}`
    };

    this.insertOrPushStep(session, step);
    this.liveExecutableSteps$.next([...session.executableSteps]);
    this.recordingSession$.next(session);
  }

  /**
   * Add a label + value verification/assertion without selecting an element.
   * Builds a verify/assert step that targets an element by its label/name and checks expected text/value.
   */
  addLabelValueVerification(config: {
    label: string;
    expected: string;
    operator?: 'equals' | 'contains' | 'containsAll' | 'matches' | 'is';
    assert?: boolean;
    timeoutMs?: number;
  }): void {
    const session = this.recordingSession$.value;
    if (!session) return;

    const label = (config.label || '').trim();
    const expected = (config.expected || '').trim();
    if (!label || !expected) return;

    const step: TestStep = {
      id: `verify-${Date.now()}`,
      action: (config.assert ? 'assert' : 'verify') as any,
      selector: {
        name: label,
        operator: config.operator || 'contains'
      } as any,
      value: expected,
      text: label,
      timeout: config.timeoutMs || 8000,
      description: `${config.assert ? 'Assert' : 'Verify'} ${label} ${(config.operator || 'contains')} ${expected}`
    };

    this.insertOrPushStep(session, step);
    this.liveExecutableSteps$.next([...session.executableSteps]);
    this.recordingSession$.next(session);
  }

  /**
   * Add a wait step that pauses execution for the given milliseconds.
   */
  addWait(ms: number): void {
    const session = this.recordingSession$.value;
    if (!session) return;

    const duration = Math.max(0, Math.floor(ms || 0));
    const step: TestStep = {
      id: `wait-${Date.now()}`,
      action: 'wait',
      selector: {},
      value: String(duration),
      text: 'wait',
      timeout: duration,
      description: `Wait for ${duration} ms`
    } as TestStep;

    session.executableSteps.push(step);
    session.metadata.totalSteps = session.executableSteps.length;
    this.liveExecutableSteps$.next([...session.executableSteps]);
    this.recordingSession$.next(session);
  }

  /**
   * Insert a wait step BEFORE the given index.
   */
  addWaitBefore(index: number, ms: number): void {
    const session = this.recordingSession$.value;
    if (!session) return;

    const duration = Math.max(0, Math.floor(ms || 0));
    const stepId = `wait-${Date.now()}`;
    const step: TestStep = {
      id: stepId,
      action: 'wait',
      selector: {},
      value: String(duration),
      text: 'wait',
      timeout: duration,
      description: `Wait for ${duration} ms`
    } as TestStep;

    const insertPos = Math.max(0, index);
    session.executableSteps.splice(insertPos, 0, step);

    // Keep raw actions aligned for downstream operations
    const raw: RecordedAction = {
      seq: insertPos + 1,
      stepId: stepId,
      timestamp: new Date().toISOString(),
      eventType: 'change',
      action: 'wait',
      element: {
        tag: 'META', type: '', id: '', name: 'wait', automationId: '', testId: '', role: '', className: '', placeholder: '', ariaLabel: '', text: ''
      },
      data: { value: String(duration) },
      url: session.baseUrl,
      pageTitle: ''
    };
    session.actions.splice(insertPos, 0, raw);

    session.metadata.totalSteps = session.executableSteps.length;
    this.liveExecutableSteps$.next([...session.executableSteps]);
    this.liveActions$.next([...session.actions]);
    this.recordingSession$.next(session);
  }

  /**
   * Insert a wait step AFTER the given index.
   */
  addWaitAfter(index: number, ms: number): void {
    const session = this.recordingSession$.value;
    if (!session) return;
    this.addWaitBefore(index + 1, ms);
  }

  /**
   * Move a step from one position to another.
   */
  moveStep(fromIndex: number, toIndex: number): void {
    const session = this.recordingSession$.value;
    if (!session) return;
    const steps = session.executableSteps;
    if (fromIndex < 0 || fromIndex >= steps.length || toIndex < 0 || toIndex >= steps.length || fromIndex === toIndex) return;

    const [step] = steps.splice(fromIndex, 1);
    steps.splice(toIndex, 0, step);

    // Keep raw actions aligned
    if (session.actions.length > fromIndex) {
      const [raw] = session.actions.splice(fromIndex, 1);
      session.actions.splice(toIndex, 0, raw);
    }

    this.liveExecutableSteps$.next([...steps]);
    this.liveActions$.next([...session.actions]);
    this.recordingSession$.next(session);
  }

  /**
   * Insert a pressEnter step AFTER the given index.
   */
  addPressEnterAfter(index: number): void {
    const session = this.recordingSession$.value;
    if (!session) return;

    const stepId = `press-enter-${Date.now()}`;
    const step: TestStep = {
      id: stepId,
      action: 'pressEnter',
      selector: {},
      value: '',
      text: '',
      description: 'Press Enter key'
    } as TestStep;

    const insertPos = Math.min(index + 1, session.executableSteps.length);
    session.executableSteps.splice(insertPos, 0, step);

    const raw: RecordedAction = {
      seq: insertPos + 1,
      stepId: stepId,
      timestamp: new Date().toISOString(),
      eventType: 'keypress',
      action: 'pressEnter',
      element: {
        tag: 'META', type: '', id: '', name: '', automationId: '', testId: '', role: '', className: '', placeholder: '', ariaLabel: '', text: ''
      },
      data: { key: 'Enter' },
      url: session.baseUrl,
      pageTitle: ''
    };
    session.actions.splice(insertPos, 0, raw);

    session.metadata.totalSteps = session.executableSteps.length;
    this.liveExecutableSteps$.next([...session.executableSteps]);
    this.liveActions$.next([...session.actions]);
    this.recordingSession$.next(session);
  }

  /**
   * Start a table validation session by capturing table metadata from the page.
   * This creates a new verifyTableMultiple step with empty validations array.
   */
  async startTableValidation(tableSelectorOrOptions?: string | { selector?: string; tableIndex?: number }): Promise<{
    success: boolean;
    tableData?: {
      tableSelector: string;
      tableId: string;
      columnNames: string[];
      columnCount: number;
      hasPagination: boolean;
    };
    stepId?: string;
    error?: string;
  }> {
    const session = this.recordingSession$.value;
    if (!session) {
      return { success: false, error: 'No active recording session' };
    }

    try {
      // Capture table metadata from the page
      const tableData = await (window as any).host?.captureTable(tableSelectorOrOptions);
      
      if (!tableData || !tableData.success) {
        return { success: false, error: tableData?.error || 'Failed to capture table' };
      }

      // Create new verifyTableMultiple step with empty validations
      const stepId = `table-verify-${Date.now()}`;
      const step: TestStep = {
        id: stepId,
        action: 'verifyTableMultiple' as any,
        selector: {
          tableSelector: '[data-automation-id="table"]',
          columnNames: tableData.columnNames || [],
          validations: [],
          searchAllPages: tableData.hasPagination || false
        } as any,
        value: '',
        text: `Table validation (0 validations)`,
        timeout: 30000,
        description: `Verify table data (0 validations pending)`
      };

      this.insertOrPushStep(session, step);
      this.liveExecutableSteps$.next([...session.executableSteps]);
      this.recordingSession$.next(session);

      return {
        success: true,
        tableData: {
          tableSelector: tableData.tableSelector,
          tableId: tableData.tableId,
          columnNames: tableData.columnNames,
          columnCount: tableData.columnCount,
          hasPagination: tableData.hasPagination
        },
        stepId
      };
    } catch (error: any) {
      return { success: false, error: error.message || 'Unknown error' };
    }
  }

  /**
   * Add a validation entry to the current table validation step.
   * Each entry specifies column-value pairs that should match in a single row.
   */
  addValidationEntry(columnValuePairs: Array<{ column: string; value: string; operator?: 'equals' | 'contains' | 'notEquals' | 'notContains' | 'greaterThan' | 'lessThan' | 'startsWith' | 'endsWith' | 'isEmpty' | 'isNotEmpty' }>, targetStepId?: string): boolean {
    const session = this.recordingSession$.value;
    if (!session || !session.executableSteps || session.executableSteps.length === 0) {
      return false;
    }

    // Find the target verifyTableMultiple step (by ID if provided, else last one)
    let tableStepIndex = -1;
    if (targetStepId) {
      tableStepIndex = session.executableSteps.findIndex(s => s.id === targetStepId);
    }
    if (tableStepIndex === -1) {
      for (let i = session.executableSteps.length - 1; i >= 0; i--) {
        if (session.executableSteps[i].action === 'verifyTableMultiple') {
          tableStepIndex = i;
          break;
        }
      }
    }

    if (tableStepIndex === -1) {
      return false;
    }

    const tableStep = session.executableSteps[tableStepIndex];

    // Deep copy selector FIRST to break shared references before mutation
    const newSelector = JSON.parse(JSON.stringify(tableStep.selector || {}));
    if (!newSelector.validations) {
      newSelector.validations = [];
    }

    // Add new validation entry to the COPY (not the shared original)
    newSelector.validations.push({
      columnPairs: columnValuePairs
    });

    const validationCount = newSelector.validations.length;

    // Replace step with new selector
    session.executableSteps[tableStepIndex] = {
      ...tableStep,
      selector: newSelector,
      text: `Table validation (${validationCount} validation${validationCount !== 1 ? 's' : ''})`,
      description: `Verify table data (${validationCount} validation${validationCount !== 1 ? 's' : ''})`
    };

    // Update session
    this.liveExecutableSteps$.next([...session.executableSteps]);
    this.recordingSession$.next(session);

    return true;
  }

  /**
   * Remove a validation entry from the current table validation step by index.
   */
  removeValidationEntry(validationIndex: number, targetStepId?: string): boolean {
    const session = this.recordingSession$.value;
    if (!session || !session.executableSteps || session.executableSteps.length === 0) {
      return false;
    }

    // Find the target verifyTableMultiple step (by ID if provided, else last one)
    let tableStepIndex = -1;
    if (targetStepId) {
      tableStepIndex = session.executableSteps.findIndex(s => s.id === targetStepId);
    }
    if (tableStepIndex === -1) {
      for (let i = session.executableSteps.length - 1; i >= 0; i--) {
        if (session.executableSteps[i].action === 'verifyTableMultiple') {
          tableStepIndex = i;
          break;
        }
      }
    }

    if (tableStepIndex === -1) {
      return false;
    }

    const tableStep = session.executableSteps[tableStepIndex];
    const validations = (tableStep.selector as any)?.validations;

    if (!validations || validationIndex < 0 || validationIndex >= validations.length) {
      return false;
    }

    // Deep copy selector FIRST to break shared references before mutation
    const newSelector = JSON.parse(JSON.stringify(tableStep.selector || {}));
    newSelector.validations.splice(validationIndex, 1);

    const validationCount = newSelector.validations.length;

    // Replace step with new selector
    session.executableSteps[tableStepIndex] = {
      ...tableStep,
      selector: newSelector,
      text: `Table validation (${validationCount} validation${validationCount !== 1 ? 's' : ''})`,
      description: `Verify table data (${validationCount} validation${validationCount !== 1 ? 's' : ''})`
    };

    // Update session
    this.liveExecutableSteps$.next([...session.executableSteps]);
    this.recordingSession$.next(session);

    return true;
  }

  /**
   * Update the searchAllPages flag for the current table validation step.
   */
  updateTableSearchAllPages(searchAllPages: boolean): boolean {
    const session = this.recordingSession$.value;
    if (!session || !session.executableSteps || session.executableSteps.length === 0) {
      return false;
    }

    // Find the last verifyTableMultiple step
    let tableStepIndex = -1;
    for (let i = session.executableSteps.length - 1; i >= 0; i--) {
      if (session.executableSteps[i].action === 'verifyTableMultiple') {
        tableStepIndex = i;
        break;
      }
    }

    if (tableStepIndex === -1) {
      return false;
    }

    const tableStep = session.executableSteps[tableStepIndex];
    
    if (!tableStep.selector) {
      tableStep.selector = {};
    }

    (tableStep.selector as any).searchAllPages = searchAllPages;

    // Update session
    this.liveExecutableSteps$.next([...session.executableSteps]);
    this.recordingSession$.next(session);

    return true;
  }

  /**
   * Remove trailing duplicate click steps that target the same selector.
   * Keeps a single click if present; removes consecutive duplicates at the end.
   */
  private removeTrailingDuplicateClicks(): void {
    const session = this.recordingSession$.value;
    if (!session || session.executableSteps.length < 2) return;

    let end = session.executableSteps.length - 1;
    // Identify the last run of consecutive clicks with the same selector css
    let i = end;
    const lastCss = session.executableSteps[i]?.selector?.css || '';
    if (!lastCss) return;

    // Walk backward through consecutive clicks with same css
    while (i >= 0) {
      const step = session.executableSteps[i];
      const css = step?.selector?.css || '';
      if (step?.action === 'click' && css === lastCss) {
        i--;
        continue;
      }
      break;
    }

    const start = i + 1; // first index of the duplicate run
    const count = end - start + 1;
    if (count > 1) {
      // Remove duplicates but keep the first occurrence
      const removeFrom = start + 1;
      const removeCount = count - 1;
      session.executableSteps.splice(removeFrom, removeCount);
      session.actions.splice(removeFrom, removeCount);
      session.metadata.totalSteps = session.executableSteps.length;
      this.liveExecutableSteps$.next([...session.executableSteps]);
      this.liveActions$.next([...session.actions]);
    }
  }

  /**
   * Check if two elements are the same
   */
  private isSameElement(elem1: RecordedAction['element'], elem2: RecordedAction['element']): boolean {
    // Check automation IDs
    if (elem1.automationId && elem1.automationId === elem2.automationId) {
      return true;
    }
    
    // Check test IDs
    if (elem1.testId && elem1.testId === elem2.testId) {
      return true;
    }
    
    // Check element IDs
    if (elem1.id && elem1.id === elem2.id) {
      return true;
    }
    
    // Check name (must be non-empty)
    if (elem1.name && elem1.name === elem2.name && elem1.name !== '') {
      return true;
    }
    
    // Check placeholder (must be non-empty)
    if (elem1.placeholder && elem1.placeholder === elem2.placeholder && elem1.placeholder !== '') {
      return true;
    }
    
    return false;
  }

  /**
   * Check if two actions are duplicates
   */
  private isDuplicateAction(action1: RecordedAction, action2: RecordedAction): boolean {
    // Must be same action type
    if (action1.action !== action2.action) return false;
    
    // Must be on same element
    if (!this.isSameElement(action1.element, action2.element)) return false;
    
    // For fill actions, check if it's just typing in progress
    if (action1.action === 'fill' && action2.action === 'fill') {
      const value1 = action1.data.value || '';
      const value2 = action2.data.value || '';
      
      // If values are exactly the same, it's a duplicate
      if (value1 === value2) return true;
      
      // If one value is a substring of the other AND they're close in time, it's typing in progress
      const time1 = new Date(action1.timestamp).getTime();
      const time2 = new Date(action2.timestamp).getTime();
      const timeDiff = Math.abs(time2 - time1);
      
      if (timeDiff < 1000 && (value1.includes(value2) || value2.includes(value1))) {
        return true;
      }
    }
    
    // For click actions, check if within 500ms (accidental double-click)
    if (action1.action === 'click' && action2.action === 'click') {
      const time1 = new Date(action1.timestamp).getTime();
      const time2 = new Date(action2.timestamp).getTime();
      const timeDiff = Math.abs(time2 - time1);
      
      return timeDiff < 500; // Half second for clicks
    }
    
    return false;
  }

  /**
   * Transform raw action to executable TestStep format
   */
  private transformToExecutableStep(action: RecordedAction): TestStep | null {
    const step: TestStep = {
      id: action.stepId,
      action: action.action as any,
      selector: this.buildSelector(action.element),
      description: this.generateStepDescription(action),
      timeout: 10000
    };

    // Add value for fill actions
    if (action.action === 'fill') {
      const raw = action.data.value;
      const val = typeof raw === 'string' ? raw.trim() : raw;
      if (val === undefined || val === null || (typeof val === 'string' && val.length === 0)) {
        // Skip creating an executable step for empty fill
        return null;
      }
      const paramName = this.extractParameterName(action.element.name || action.element.placeholder || action.element.id);
      
      // Store the REAL value for execution, and a masked displayValue for UI if password
      step.value = String(val);
      if (action.data.isPassword && paramName) {
        (step as any).displayValue = `{{${paramName}}}`;
      }
      // Prefer associated label text over random IDs or empty names
      step.text = (action.element as any)['labelText'] || action.element.name || action.element.placeholder || action.element.ariaLabel;

      // Store parameter for data-driven testing
      if (paramName) {
        const session = this.recordingSession$.value;
        if (session) {
          session.dataParameters[paramName] = {
            defaultValue: String(val),
            type: action.data.isPassword ? 'password' : 'string',
            required: true,
            description: `Value for ${action.element.name || 'field'}`
          };
        }
      }
    }

    // Add text for click actions (for element matching)
    if (action.action === 'click') {
      // Prefer the labelText if available to avoid random IDs appearing in step names
      step.text = (action.element as any)['labelText'] || action.element.text || action.element.name || action.element.ariaLabel;
    }

    // Add value for select actions
    if (action.action === 'select' && action.data.selectedValue) {
      step.value = action.data.selectedValue;
      step.text = action.data.selectedText;
    }

    // Add check/uncheck action data (tag name for Workday checkbox)
    if (action.action === 'check' || action.action === 'uncheck') {
      step.text = action.data?.text || action.data?.rowLabel || '';
      (step as any).checkData = {
        text: action.data?.text || '',
        rowLabel: action.data?.rowLabel || '',
      };
    }

    return step;
  }

  /**
   * Build comprehensive selector for element with advanced fallback strategy
   */
  private buildSelector(element: RecordedAction['element']): TestStep['selector'] {
    const selector: any = {};
    const tagLower = element.tag.toLowerCase();

    // Always include expected tag for execution-time filtering
    selector.expectedTag = tagLower;

  // Pass through section context if present from recorder
  const sectionContext = (element as any).sectionLabel ? String((element as any).sectionLabel).trim() : '';
  const subSectionContext = (element as any).subSectionLabel ? String((element as any).subSectionLabel).trim() : '';
  const dialogTitle = (element as any).dialogTitle ? String((element as any).dialogTitle).trim() : '';
    const listContext = (element as any).listContext ? String((element as any).listContext).trim() : '';
    const listContextSection = (element as any).listContextSection ? String((element as any).listContextSection).trim() : '';
    const rowLabel = (element as any).rowLabel ? String((element as any).rowLabel).trim() : '';

    // Priority 0: ALWAYS use the CSS path from recorder if available (most accurate)
    // The recorder generated this from the ACTUAL element that was clicked
    if ((element as any).cssPath) {
      const cssPath = (element as any).cssPath;
      
      // Check if cssPath is actually an XPath (starts with // or /)
      const isXPath = typeof cssPath === 'string' && (cssPath.startsWith('//') || cssPath.startsWith('/'));
      
      if (isXPath) {
        selector.xpath = cssPath;
        // Persist stable name and contexts even when primary path is XPath
        selector.name = (element as any)['labelText'] || element.name || element.ariaLabel || element.placeholder;
        if (sectionContext) {
          (selector as any).sectionContext = sectionContext;
        }
        if (subSectionContext) {
          (selector as any).subSectionContext = subSectionContext;
        }
        if (dialogTitle) {
          (selector as any).dialogTitle = dialogTitle;
        }
        if (listContext) {
          (selector as any).listContext = listContext;
        }
        if (listContextSection) {
          (selector as any).listContextSection = listContextSection;
        }
        if (rowLabel) {
          (selector as any).rowLabel = rowLabel;
        }

        // Try to use fallback CSS selectors if available
        if ((element as any).cssFallbacks && (element as any).cssFallbacks.length > 0) {
          selector.css = (element as any).cssFallbacks[0];
        } else {
          // No CSS available, need to build one from element attributes
          // Don't return yet, fall through to build a selector
        }
      } else {
        selector.css = cssPath;
  // Prefer a text-aware XPath fallback when available (e.g., promptOption by visible text)
  const recordedXpath = (element as any).xpath;
  const xpathFallback = ((element as any).cssFallbacks || []).find((s: string) => typeof s === 'string' && (s.startsWith('//') || s.startsWith('/')));
  selector.xpath = xpathFallback || recordedXpath;
        
        // IMPORTANT: Include fallback selectors for better reliability
        if ((element as any).cssFallbacks && (element as any).cssFallbacks.length > 0) {
          (selector as any).cssFallbacks = (element as any).cssFallbacks;
        }
        
        // Include fingerprint for disambiguation
        if ((element as any).fingerprint) {
          (selector as any).fingerprint = (element as any).fingerprint;
        }
        
        // Extract automation-id from cssPath if present
        const automationIdMatch = selector.css.match(/data-automation-id="([^"]+)"/);
        if (automationIdMatch) {
          selector.automationId = automationIdMatch[1];
        }
        
        // Extract uxi-element-id from cssPath if present
        const uxiIdMatch = selector.css.match(/data-uxi-element-id="([^"]+)"/);
        if (uxiIdMatch) {
          selector.uxiElementId = uxiIdMatch[1];
        }
        
        // Prefer stable labelText as name fallback
        selector.name = (element as any)['labelText'] || element.name || element.ariaLabel || element.placeholder;
        // Persist section context to help disambiguate repeated labels (e.g., Phone vs Address)
        if (sectionContext) {
          (selector as any).sectionContext = sectionContext;
        }
        // Persist optional sub-section and dialog context for future executor filters
        if (subSectionContext) {
          (selector as any).subSectionContext = subSectionContext;
        }
        if (dialogTitle) {
          (selector as any).dialogTitle = dialogTitle;
        }
        if (listContext) {
          (selector as any).listContext = listContext;
        }
        if (listContextSection) {
          (selector as any).listContextSection = listContextSection;
        }
        if (rowLabel) {
          (selector as any).rowLabel = rowLabel;
        }
        // Only use preferFirstMatch for input-like elements with known duplicate selectors
        const isInputLike = ['input','textarea','select'].includes(tagLower);
        const likelyDuplicate = /textInputBox|search(Input|Box)|globalSearch/i.test(cssPath);
        if (isInputLike && likelyDuplicate) {
          // Disable preferFirstMatch when label is generic or section context is available to avoid picking first occurrence
          const labelLower = ((element as any)['labelText'] || '').toString().toLowerCase().trim();
          const isGeneric = /^(type|state|city|county|code|name|value|title|description|category)$/i.test(labelLower);
          (selector as any).preferFirstMatch = !(isGeneric || !!sectionContext);
        }
  return this.specializePromptOptionSelector(element, selector);
      }
    }

    // Priority 1: data-automation-id (only if CSS path not available)
    if (element.automationId) {
      selector.automationId = element.automationId;
      selector.css = `${tagLower}[data-automation-id="${element.automationId}"]`;
      if (sectionContext) {
        (selector as any).sectionContext = sectionContext;
      }
      if (subSectionContext) {
        (selector as any).subSectionContext = subSectionContext;
      }
      if (dialogTitle) {
        (selector as any).dialogTitle = dialogTitle;
      }
      if (listContext) {
        (selector as any).listContext = listContext;
      }
      if (listContextSection) {
        (selector as any).listContextSection = listContextSection;
      }
      if (rowLabel) {
        (selector as any).rowLabel = rowLabel;
      }
      // Prefer first match ONLY for input-like fields with known duplicated automation-ids
      const isInputLike = ['input','textarea','select'].includes(tagLower);
      const autoId = element.automationId || '';
      if (isInputLike && (/^textInputBox$/i.test(autoId) || /^search(Input|Box)$/i.test(autoId) || /search/i.test(autoId))) {
        const labelLower = ((element as any)['labelText'] || '').toString().toLowerCase().trim();
        const isGeneric = /^(type|state|city|county|code|name|value|title|description|category)$/i.test(labelLower);
        (selector as any).preferFirstMatch = !(isGeneric || !!sectionContext);
      }
  return this.specializePromptOptionSelector(element, selector);
    }

    // Priority 2: data-testid
    if (element.testId) {
      selector.testAttr = { name: 'data-testid', value: element.testId };
      selector.css = `${tagLower}[data-testid="${element.testId}"]`;
  return this.specializePromptOptionSelector(element, selector);
    }

    // Priority 3: data-uxi-element-id (Workday-specific)
    if ((element as any).uxiElementId) {
      selector.uxiElementId = (element as any).uxiElementId;
      selector.css = `${tagLower}[data-uxi-element-id="${(element as any).uxiElementId}"]`;
  return this.specializePromptOptionSelector(element, selector);
    }

    // Priority 4: aria-label + role (semantic, stable)
    if (element.ariaLabel && element.role) {
      selector.role = element.role;
      selector.ariaLabel = element.ariaLabel;
      selector.css = `${tagLower}[role="${element.role}"][aria-label="${element.ariaLabel}"]`;
  return this.specializePromptOptionSelector(element, selector);
    }

    // Priority 5: name attribute
    if (element.name) {
      selector.name = element.name;
      selector.css = `${element.tag.toLowerCase()}[name="${element.name}"]`;
  return this.specializePromptOptionSelector(element, selector);
    }

    // Priority 6: placeholder
    if (element.placeholder) {
      selector.placeholder = element.placeholder;
      selector.css = `${element.tag.toLowerCase()}[placeholder="${element.placeholder}"]`;
  return this.specializePromptOptionSelector(element, selector);
    }

    // Priority 7: Parent aria-label context
    if ((element as any).parentAriaLabel) {
      selector.parentContext = (element as any).parentAriaLabel;
      selector.css = `[aria-label="${(element as any).parentAriaLabel}"] ${element.tag.toLowerCase()}`;
  return this.specializePromptOptionSelector(element, selector);
    }

    // Priority 8: ID (may be generated, less stable)
    // Avoid generated/dynamic IDs (e.g., contains $ or long random suffixes)
    if (element.id && !this.isGeneratedId(element.id)) {
      selector.id = element.id;
      selector.css = `#${element.id}`;
      selector.warning = 'ID-based selector may be unstable';
    }

    // Fallback: Build from available attributes
    if (!selector.css) {
      selector.css = this.buildCssSelector(element);
    }

    // Always add role for semantic backup
    if (element.role) {
      selector.role = element.role;
    }

  return this.specializePromptOptionSelector(element, selector);
  }
  
  /**
   * Small specialization: if clicking a prompt option, inherit context from the active list/field
   */
  private specializePromptOptionSelector(element: RecordedAction['element'], selector: any): any {
    const isPromptOption = (element.automationId || '').toLowerCase() === 'promptoption' || ((element as any).fingerprint?.tag === 'div' && (element as any).fingerprint?.className?.includes('LabelContainer'));
    if (!isPromptOption) return selector;

    const listContext = (element as any).listContext ? String((element as any).listContext).trim() : '';
    const listContextSection = (element as any).listContextSection ? String((element as any).listContextSection).trim() : '';
    if (listContextSection) {
      selector.sectionContext = listContextSection; // prefer the originating field's section (e.g., Address)
    }
    if (listContext) {
      selector.name = listContext; // e.g., Type
    }
    // Avoid picking the first match when multiple lists exist
    selector.preferFirstMatch = false;
    // Ensure we always have a stable fallback for prompt options instead of ID-specific selectors
    // Many Workday prompt options are rendered with dynamic IDs like "#promptOption-gwt-uid-3".
    // When the target text is edited (e.g., choosing a different row), we need the executor
    // to be able to locate any prompt option and then filter by the desired text.
    const hasFallbacks = Array.isArray((selector as any).cssFallbacks);
    const hasPromptOptionFallback = hasFallbacks && (selector as any).cssFallbacks.some((s: string) => typeof s === 'string' && s.includes('data-automation-id="promptOption"'));
    if (!hasPromptOptionFallback) {
      (selector as any).cssFallbacks = [
        ...(((selector as any).cssFallbacks) || []),
        '[data-automation-id="promptOption"]'
      ];
    }
    return selector;
  }
  /**
   * Detect likely generated IDs to avoid using them in selectors
   */
  private isGeneratedId(id: string): boolean {
    if (!id) return true;
    // Common random patterns: long hex chunks, GUID-like, Workday-style with $ and random tail
    if (id.includes('$')) return true;
    if (/^[a-f0-9\-]{16,}$/i.test(id)) return true;
    if (/\-[a-f0-9]{8,}$/i.test(id)) return true;
    if (/^.+\-input$/.test(id) && /[a-f0-9]{6,}/i.test(id)) return true;
    return false;
  }

  /**
   * Build CSS selector from element properties
   */
  private buildCssSelector(element: RecordedAction['element']): string {
    const parts: string[] = [];

    // Tag name
    parts.push(element.tag.toLowerCase());

    // ID
    if (element.id) {
      parts.push(`#${element.id}`);
    }

    // Classes (first 2)
    if (element.className) {
      const classes = element.className.split(/\s+/).filter(c => c).slice(0, 2);
      if (classes.length > 0) {
        parts.push('.' + classes.join('.'));
      }
    }

    // Type attribute for inputs
    if (element.type && element.tag === 'INPUT') {
      parts.push(`[type="${element.type}"]`);
    }

    return parts.join('');
  }

  /**
   * Generate human-readable step description with actual values
   */
  private generateStepDescription(action: RecordedAction): string {
    const elementName = (action.element as any)['labelText'] ||
                       action.element.name || 
                       action.element.placeholder || 
                       action.element.ariaLabel || 
                       action.element.text ||
                       'element';

    switch (action.action) {
      case 'fill':
        // Include actual value in description (masked for passwords)
        const actualValue = action.data.isPassword ? 
          '{{password}}' : 
          (action.data.value || 'value');
        return `Enter the ${elementName} as ${actualValue}`;
      
      case 'click':
        const rowLbl = ((action.element as any).rowLabel || '').trim();
        return rowLbl ? `Click ${elementName} (${rowLbl})` : `Click ${elementName}`;
      
      case 'select':
        const selectedValue = action.data.selectedText || action.data.selectedValue || 'option';
        return `Select ${selectedValue} in ${elementName}`;
      
      case 'check':
        const checkLabel = action.data?.text || action.data?.rowLabel || elementName;
        return `Check ${checkLabel}`;
      
      case 'uncheck':
        return `Uncheck ${elementName}`;
      
      case 'pressEnter':
        return 'Press Enter key';
      
      case 'navigate':
        return `Navigate to ${action.url}`;
      
      case 'wait':
        return 'Wait for page to load';
      
      default:
        return `Perform ${action.action} on ${elementName}`;
    }
  }

  /**
   * Extract parameter name from element name
   */
  private extractParameterName(name: string): string {
    if (!name) return '';
    
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '');
  }

  /**
   * Set up permanent listener for action events from Electron (called once in constructor)
   */
  private setupActionListener(): void {
    // This will be called by Electron when actions are captured
    (window as any).host?.onActionCaptured((actionData: any) => {
      this.addAction(actionData);
    });
  }

  /**
   * Save recording to JSON file
   */
  // private async saveRecording(session: RecordingSession): Promise<string> {
  //   const recordingData = {
  //     recordingId: session.recordingId,
  //     runName: session.runName,
  //     recordedAt: session.recordedAt,
  //     recordedBy: session.recordedBy,
  //     baseUrl: session.baseUrl,
  //     steps: session.executableSteps,
  //     dataParameters: session.dataParameters,
  //     metadata: session.metadata,
  //     rawActions: session.actions // Keep for reference
  //   };

  //   // Save via Electron IPC
  //   const result = await (window as any).host?.saveRecording(recordingData);
  //   return result?.filePath || '';
  // }

  // /**
  //  * Load existing recording
  //  */
  // async loadRecording(recordingId: string): Promise<RecordingSession | null> {
  //   const result = await (window as any).host?.loadRecording(recordingId);
  //   if (result?.success && result.data) {
  //     const session: RecordingSession = {
  //       recordingId: result.data.recordingId,
  //       runName: result.data.runName,
  //       recordedAt: result.data.recordedAt,
  //       recordedBy: result.data.recordedBy,
  //       baseUrl: result.data.baseUrl,
  //       status: 'completed',
  //       actions: result.data.rawActions || [],
  //       executableSteps: result.data.steps || [],
  //       dataParameters: result.data.dataParameters || {},
  //       metadata: result.data.metadata || { totalSteps: 0, estimatedDuration: 0, tags: [] }
  //     };
  //     return session;
  //   }
  //   return null;
  // }

  /**
   * Delete a step from the current recording
   */
  deleteStep(index: number): void {
    const session = this.recordingSession$.value;
    if (!session) {
      return;
    }

    // Only delete from executableSteps, don't touch actions array
    // Actions array may not match steps 1:1 (multiple actions can create one step)
    if (index >= 0 && index < session.executableSteps.length) {
      // Create a new array without the deleted step
      const updatedSteps = [...session.executableSteps];
      updatedSteps.splice(index, 1);
      
      // Update session with new array
      session.executableSteps = updatedSteps;
      session.metadata.totalSteps = updatedSteps.length;

      // Emit the updated steps and session
      this.liveExecutableSteps$.next(updatedSteps);
      this.recordingSession$.next({ ...session });
    } else {
      console.warn('[ActionRecorder] Invalid index for deleteStep:', index, 'Valid range: 0-', session.executableSteps.length - 1);
    }
  }

  /**
   * Edit a step's description or value
   */
  updateStep(index: number, updates: Partial<TestStep>): void {
    const session = this.recordingSession$.value;
    if (!session || !session.executableSteps[index]) return;

    const original = session.executableSteps[index];
    const next: TestStep = { ...original, ...updates } as TestStep;

    // If user updates a value on a password-like field, preserve or add masked displayValue
    if (updates.value !== undefined) {
      const looksPassword = /password|pwd/i.test(original.selector?.name || '') || /password|pwd/i.test(original.description || '');
      const hasDisplay = (original as any).displayValue || (next as any).displayValue;
      if (looksPassword || hasDisplay) {
        (next as any).displayValue = (original as any).displayValue || '{{password}}';
      }
    }

    // If user edits the target text for a click/select, also rewrite selector to avoid hard-coded IDs
    // and ensure we can match the new text (especially for Workday prompt options).
    if ((updates.text !== undefined && updates.text !== null) && (original.action === 'click' || original.action === 'select')) {
      const newText = String(updates.text || '').trim();
      const prevText = String(original.text || original.selector?.name || '').trim();
      const sel: any = { ...(original.selector || {}) };

      // Only act when text actually changed
      if (newText && newText !== prevText) {
        // 1) If selector is a Workday prompt option or looks like one, normalize it to use stable fallback
        const isPromptOption = (
          (sel.automationId || '').toLowerCase() === 'promptoption' ||
          (typeof sel.css === 'string' && sel.css.includes('data-automation-id') && sel.css.includes('promptOption')) ||
          (typeof sel.xpath === 'string' && sel.xpath.includes("@data-automation-id='promptOption'"))
        );

        // Remove dynamic GWT IDs like #promptOption-gwt-uid-3
        if (typeof sel.css === 'string' && /^#promptOption-gwt-uid-/i.test(sel.css)) {
          sel.css = undefined; // do not keep unstable ids
        }

        // CHANGE #1: If CSS is generic promptOption, unset it so XPath becomes primary
        if (typeof sel.css === 'string') {
          const cssLower = sel.css.toLowerCase();
          if (cssLower === 'div[data-automation-id="promptoption"]' || cssLower.includes('data-automation-id="promptoption"')) {
            sel.css = undefined;
          }
        }

        // 2) Build a dynamic XPath that matches the edited text
        // CHANGE #2: Build robust XPath using main name + optional qualifier (inside parentheses)
        const buildPromptXPath = (text: string) => {
          const full = (text || '').trim();
          const m = full.match(/^(.*?)\s*\((.+)\)\s*$/); // e.g., "Alex Flye (applying for: Computer Science Major)"
          let main = full;
          let qual = '';
          if (m) {
            main = (m[1] || '').trim();
            qual = (m[2] || '').trim();
            // Strip leading prefixes like "applying for:" in qualifier
            const qMatch = qual.match(/^[A-Za-z\s]*:\s*(.+)$/);
            if (qMatch) {
              qual = qMatch[1].trim();
            }
          }
          const safeMain = main.replace(/'/g, "\\'");
          const safeQual = qual.replace(/'/g, "\\'");
          let xp = `//div[@data-automation-id='promptOption'][contains(normalize-space(.), '${safeMain}')]`;
          if (safeQual) {
            xp = `${xp}[contains(normalize-space(.), '${safeQual}')]`;
          }
          return xp;
        };

        // If existing xpath has contains(normalize-space(.), '<old>'), replace it; else add one
        // Always prefer a freshly built XPath for edited prompt options
        sel.xpath = buildPromptXPath(newText);

        // 3) Update cssFallbacks: remove entries tied to previous text; ensure promptOption + dynamic XPath present
        const fallbacks: string[] = Array.isArray(sel.cssFallbacks) ? sel.cssFallbacks.slice() : [];
        const prevLower = prevText.toLowerCase();
        const filtered = fallbacks.filter(f => {
          const s = String(f || '').toLowerCase();
          // Drop fallbacks that hard-code previous text in aria-label or XPath contains
          return !(s.includes(prevLower) || /contains\(\s*normalize-space\(.*\),\s*'[^']*'\s*\)/.test(s));
        });
        // Ensure stable promptOption fallback
        if (!filtered.some(s => typeof s === 'string' && s.includes('data-automation-id="promptOption"'))) {
          filtered.push('[data-automation-id="promptOption"]');
        }
        // Ensure a dynamic XPath for the new text
        const dynXPath = buildPromptXPath(newText);
        if (!filtered.some(s => typeof s === 'string' && s === dynXPath)) {
          filtered.push(dynXPath);
        }
        sel.cssFallbacks = filtered;

        // 4) Keep selector.name in sync with edited text (helps disambiguation)
        sel.name = newText;
        // Prompt options should not prefer first match
        if (isPromptOption) sel.preferFirstMatch = false;

        // CHANGE #3: Clear only unstable identifiers; PRESERVE section context for disambiguation
        try {
          delete sel.fingerprint;
          // Preserve section-related context to keep section-aware matching
          // delete sel.sectionContext; // DO NOT DELETE
          // delete sel.subSectionContext; // DO NOT DELETE
          // delete sel.listContextSection; // DO NOT DELETE
          delete sel.ariaLabel; // ariaLabel from record may bias incorrectly; allow runner to use live aria-label
        } catch {}

        // Safety: if section context was present on original selector, ensure it remains set
        if (!(sel as any).sectionContext && (original.selector as any)?.sectionContext) {
          (sel as any).sectionContext = (original.selector as any).sectionContext;
        }
        if (!(sel as any).subSectionContext && (original.selector as any)?.subSectionContext) {
          (sel as any).subSectionContext = (original.selector as any).subSectionContext;
        }
        if (!(sel as any).listContextSection && (original.selector as any)?.listContextSection) {
          (sel as any).listContextSection = (original.selector as any).listContextSection;
        }

        next.selector = sel;
        next.description = (original.description || '').replace(prevText, newText);
      }
    }

    // Mark as modified and track changed fields
    try {
      const modifiedFields = new Set<string>(Array.isArray(original.modifiedFields) ? original.modifiedFields : []);
      if (updates.text !== undefined && updates.text !== original.text) modifiedFields.add('text');
      if (updates.value !== undefined && updates.value !== original.value) modifiedFields.add('value');
      if (updates.selector !== undefined) modifiedFields.add('selector');
      if (updates.description !== undefined && updates.description !== original.description) modifiedFields.add('description');
      next.modified = true;
      next.modifiedFields = Array.from(modifiedFields);
      next.lastModifiedAt = new Date().toISOString();
    } catch {}

    session.executableSteps[index] = next;

    this.liveExecutableSteps$.next([...session.executableSteps]);
    this.recordingSession$.next(session);
  }

  /**
   * Clone an executable step and insert the copy right after the original.
   * Does not modify raw actions; useful for reusing verify/assert/fill steps.
   */
  cloneStep(index: number): void {
    const session = this.recordingSession$.value;
    if (!session || !session.executableSteps[index]) return;

    const original = session.executableSteps[index];
    const copy: TestStep = {
      ...JSON.parse(JSON.stringify(original)),
      id: (original.id || 'step-') + '-copy-' + Date.now(),
      description: (original.description || 'Step') + ' (copy)'
    };

    session.executableSteps.splice(index + 1, 0, copy);
    session.metadata.totalSteps = session.executableSteps.length;

    this.liveExecutableSteps$.next([...session.executableSteps]);
    this.recordingSession$.next(session);
  }

  /** Begin insert-record mode: insert the next captured action AFTER the given index */
  beginInsertRecord(afterIndex: number): void {
    if (afterIndex < -1) return;
    this.insertRecordIndex = afterIndex;
  }
  // (Re-record and insert-before logic removed)

  /**
   * Generate unique ID
   */
  private generateId(): string {
    return `rec-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get current session value (synchronous)
   */
  getCurrentSession(): RecordingSession | null {
    return this.recordingSession$.value;
  }

  /**
   * Clear current session
   */
  clearSession(): void {
    this.recordingSession$.next(null);
    this.isRecording$.next(false);
    this.liveActions$.next([]);
    this.liveExecutableSteps$.next([]);
  }

  /** Get a data parameter's current value (defaultValue) from the active session */
  getParameterValue(name: string): string | undefined {
    const session = this.recordingSession$.value;
    if (!session || !name) return undefined;
    const param = session.dataParameters?.[name];
    const val = param?.defaultValue;
    return typeof val === 'string' && val.length > 0 ? val : undefined;
  }

  /** Set or update a data parameter value in the active session and propagate change */
  setParameterValue(name: string, value: string): void {
    const session = this.recordingSession$.value;
    if (!session || !name) return;
    const existing = session.dataParameters?.[name] || {};
    const isPassword = /password/i.test(name) || /pwd/i.test(name);
    session.dataParameters[name] = {
      defaultValue: value,
      type: existing.type || (isPassword ? 'password' : 'string'),
      required: existing.required ?? true,
      description: existing.description || `Value for ${name}`
    };
    // Emit updated session to subscribers so UI reflects changes and save persists
    this.recordingSession$.next({ ...session });
  }

  
}